import {
    getRawValue,
    WorkflowActionId,
    WorkflowExecutionId,
    WorkflowId,
} from "@sp-crm/core";
import {
    AdvancedSearchCondition,
    AdvancedSearchConditionNodeType,
    AdvancedSearchEntityType,
    AdvancedSearchRequest,
    AdvancedSearchRequestType,
    AdvancedSearchSelect,
    FieldConditionOperator,
    WorkflowExecutionResult,
} from "generated/graphql";

export type WorkflowRunCountKind = "scheduled" | "completed" | "attention" | "cancelled";

export const workflowRunCountKinds: readonly WorkflowRunCountKind[] = [
    "scheduled",
    "completed",
    "attention",
    "cancelled",
];

export function workflowRunCountTitle(kind: WorkflowRunCountKind): string {
    switch (kind) {
        case "scheduled":
            return "Scheduled";
        case "completed":
            return "Completed";
        case "attention":
            return "Needs attention";
        case "cancelled":
            return "Cancelled";
    }
}

export function workflowRunEntityField(attachTo: AdvancedSearchEntityType): string {
    switch (attachTo) {
        case AdvancedSearchEntityType.HomeCareLead:
            return "homeCareLead.selfContactEntity.name";
        case AdvancedSearchEntityType.ReferenceBusiness:
            return "referenceBusiness.businessContactEntity.name";
        case AdvancedSearchEntityType.ReferenceContact:
            return "referenceContact.contactEntity.name";
        default:
            return "client.name";
    }
}

const belongsToWorkflow = (workflowId: WorkflowId): AdvancedSearchCondition => ({
    nodeType: AdvancedSearchConditionNodeType.RelationCondition,
    fieldName: "workflow",
    operator: FieldConditionOperator.RelationMatches,
    children: [
        {
            nodeType: AdvancedSearchConditionNodeType.FieldCondition,
            fieldName: "id",
            operator: FieldConditionOperator.IdEquals,
            textValue: workflowId,
        },
    ],
});

const hasResult = (result: WorkflowExecutionResult): AdvancedSearchCondition => ({
    nodeType: AdvancedSearchConditionNodeType.FieldCondition,
    fieldName: "result",
    operator: FieldConditionOperator.StringEquals,
    textValue: result,
});

const isSet = (fieldName: string): AdvancedSearchCondition => ({
    nodeType: AdvancedSearchConditionNodeType.FieldCondition,
    fieldName,
    operator: FieldConditionOperator.IsSet,
});

const scheduledRun = (): AdvancedSearchCondition[] => [
    hasResult(WorkflowExecutionResult.Pending),
    isSet("scheduledAt"),
];

const countConditions = (kind: WorkflowRunCountKind): AdvancedSearchCondition[] => {
    switch (kind) {
        case "scheduled":
            return scheduledRun();
        case "completed":
            return [hasResult(WorkflowExecutionResult.Success)];
        case "attention":
            return [
                {
                    nodeType: AdvancedSearchConditionNodeType.Or,
                    children: [
                        hasResult(WorkflowExecutionResult.Error),
                        hasResult(WorkflowExecutionResult.Warning),
                    ],
                },
            ];
        case "cancelled":
            return [hasResult(WorkflowExecutionResult.Cancelled)];
    }
};

const workflowRunsRequest = (
    workflowId: WorkflowId,
    conditions: AdvancedSearchCondition[],
    select: AdvancedSearchSelect[],
    sort: AdvancedSearchRequest["sort"],
    pageSize: number,
): AdvancedSearchRequest => ({
    entityType: AdvancedSearchEntityType.WorkflowExecution,
    type: AdvancedSearchRequestType.Implicit,
    condition: {
        nodeType: AdvancedSearchConditionNodeType.And,
        children: [belongsToWorkflow(workflowId), ...conditions],
    },
    select,
    sort,
    pageSize,
});

export function workflowRunCountRequest(
    workflowId: WorkflowId,
    kind: WorkflowRunCountKind,
): AdvancedSearchRequest {
    return workflowRunsRequest(
        workflowId,
        countConditions(kind),
        [{ fieldName: "id" }],
        { column: "id", ascending: true },
        1,
    );
}

export interface WorkflowRunsSource {
    workflowId: WorkflowId;
    attachTo: AdvancedSearchEntityType;
}

const RUNS_PAGE_SIZE = 25;

const runSelect = (source: WorkflowRunsSource): AdvancedSearchSelect[] => [
    { fieldName: workflowRunEntityField(source.attachTo) },
    { fieldName: "actionId" },
];

export function upcomingWorkflowRunsRequest(
    source: WorkflowRunsSource,
): AdvancedSearchRequest {
    return workflowRunsRequest(
        source.workflowId,
        scheduledRun(),
        [...runSelect(source), { fieldName: "scheduledAt" }],
        { column: "scheduledAt", ascending: true },
        RUNS_PAGE_SIZE,
    );
}

export function recentWorkflowRunsRequest(
    source: WorkflowRunsSource,
): AdvancedSearchRequest {
    return workflowRunsRequest(
        source.workflowId,
        [isSet("finishedAt")],
        [...runSelect(source), { fieldName: "finishedAt" }, { fieldName: "result" }],
        { column: "finishedAt", ascending: false },
        RUNS_PAGE_SIZE,
    );
}

export function withMoreWorkflowRuns(
    request: AdvancedSearchRequest,
): AdvancedSearchRequest {
    return {
        ...request,
        pageSize: (request.pageSize ?? RUNS_PAGE_SIZE) + RUNS_PAGE_SIZE,
    };
}

export interface WorkflowRunListItem {
    id: WorkflowExecutionId;
    appLink: string;
    name: string;
    actionId: WorkflowActionId | null;
    scheduledAt: string | null;
    finishedAt: string | null;
    result: WorkflowExecutionResult | null;
}

const UNNAMED_RECORD = "(no name)";

const stringOrNull = (value: unknown): string | null =>
    typeof value === "string" && value !== "" ? value : null;

const resultOrNull = (value: unknown): WorkflowExecutionResult | null => {
    const results: string[] = Object.values(WorkflowExecutionResult);
    return typeof value === "string" && results.includes(value)
        ? (value as WorkflowExecutionResult)
        : null;
};

export function workflowRunsFromSearch(
    entities: readonly Record<string, unknown>[],
    attachTo: AdvancedSearchEntityType,
): WorkflowRunListItem[] {
    const nameField = workflowRunEntityField(attachTo);
    return entities.map(entity => ({
        id: entity.id as WorkflowExecutionId,
        appLink: stringOrNull(entity.appLink) ?? "",
        name:
            stringOrNull(getRawValue(entity, nameField).getOrElse(null)) ??
            UNNAMED_RECORD,
        actionId: stringOrNull(entity.actionId) as WorkflowActionId | null,
        scheduledAt: stringOrNull(entity.scheduledAt),
        finishedAt: stringOrNull(entity.finishedAt),
        result: resultOrNull(entity.result),
    }));
}
